import { api } from '@/api/api';
import { apis } from '@/config';
import { ContactTransformInstance } from './contact_transform';
import ContactUtilInterface from './contact_util';
import { ContactDBBase } from './contact_dbl_base';
import { ContactDBMark } from './contact_dbl_mark';
import { ContactDBFrequent } from './contact_dbl_frequent';
import { ContactDBSearch } from './contact_dbl_search';
import { ContactDBPersonal } from './contact_dbl_personal';
import { ContactDBTeam } from './contact_dbl_team';
import { ContactDBEnterprise } from './contact_dbl_enterprise';
import { ContactEventHub } from './util/event_hub';
import { dispatchContactSyncEvents } from './util/contact_notify_helper';
import { ContactIMHelper, setContactIMHelperInstance, getContactIMHelperInstance } from './contact_im_helper';
import { backfillLxInfoFromDexie } from './contact_sync_enterprise_loki';
export class ContactDB {
    constructor() {
        this.dbApi = api.requireLogicalApi(apis.dbInterfaceApiImpl);
        this.httpApi = api.getDataTransApi();
        this.systemApi = api.getSystemApi();
        this.eventApi = api.getEventApi();
        this.accountApi = api.requireLogicalApi(apis.accountApiImpl);
        this.storeApi = api.getDataStoreApi();
        this.contactTrans = ContactTransformInstance;
        this.contactUtil = ContactUtilInterface;
        this.orgTable = 'org';
        this.inited = false;
        this.contactSyncTimes = 0;
        this.tablesName = [
            ContactUtilInterface.contactTable,
            ContactUtilInterface.contactItemTable,
            ContactUtilInterface.orgTable,
            ContactUtilInterface.orgpathlistTable,
        ];
        this.isSyncTeamListRef = { value: false };
        this._initSubModules();
        ContactUtilInterface.getHttpApi = () => this.httpApi;
        ContactUtilInterface.getApis = () => ({ taskApi: api.requireLogicalApi(apis.taskApiImpl) });
    }
    get enterpriseModule() {
        return this._enterprise;
    }
    _initSubModules() {
        const ctx = {
            dbApi: api.requireLogicalApi(apis.dbInterfaceApiImpl),
            lokiApi: api.requireLogicalApi(apis.dbMemoryApiImpl),
            httpApi: api.getDataTransApi(),
            taskApi: api.requireLogicalApi(apis.taskApiImpl),
            contactUtil: ContactUtilInterface,
            contactTrans: ContactTransformInstance,
            eventApi: api.getEventApi(),
            systemApi: api.getSystemApi(),
            dataTrackerApi: api.requireLogicalApi(apis.dataTrackerApiImp),
            getEnablePutContact: () => { var _a, _b; return (_b = (_a = this._search) === null || _a === void 0 ? void 0 : _a.getContactWritePriority()) !== null && _b !== void 0 ? _b : true; },
            eventHub: new ContactEventHub(api.getEventApi()),
        };
        this._base = new ContactDBBase(ctx);
        this._mark = new ContactDBMark(ctx);
        this._frequent = new ContactDBFrequent(ctx);
        this._search = new ContactDBSearch(ctx);
        this._personal = new ContactDBPersonal(ctx, this._base, this._search, this._mark);
        this._team = new ContactDBTeam(ctx, this._base, this._search);
        this._enterprise = new ContactDBEnterprise(ctx, this._base);
        const imHelper = new ContactIMHelper(this._team);
        setContactIMHelperInstance(imHelper);
    }
    setTracker(tracker) {
        if (this._personal)
            this._personal.ctx.tracker = tracker;
        if (this._enterprise)
            this._enterprise.ctx.tracker = tracker;
    }
    getContactWritePriority() {
        return this._search.getContactWritePriority();
    }
    getContactLastUpdateTime(_account) {
        return this._search.getContactLastUpdateTime(_account);
    }
    setContactLastUpdateTime(_account) {
        return this._search.setContactLastUpdateTime(_account);
    }
    setDataOrder(params) {
        return this._search.setDataOrder(params);
    }
    repackageQueryConfigByRelateEnterpriseInfo(_queryConfig, noRelateEnterprise = false, enterpriseFieldName = 'enterpriseId') {
        return this._search.repackageQueryConfigByRelateEnterpriseInfo(_queryConfig, noRelateEnterprise, enterpriseFieldName);
    }
    async getContactList(params) {
        return this._search.getContactList(params);
    }
    async getPersonalContactByEmails(emails, _account) {
        return this._search.getPersonalContactByEmails(emails, _account);
    }
    async getOrgList(params, returnEmptyWhenNoIdList) {
        return this._search.getOrgList(params, returnEmptyWhenNoIdList);
    }
    async getOrgContactList(condition) {
        return this._search.getOrgContactList(condition);
    }
    getOrgContactListByContactId(params) {
        return this._search.getOrgContactListByContactId(params);
    }
    async getOrgContactListByOrgId(idList, _account) {
        return this._search.getOrgContactListByOrgId(idList, _account);
    }
    async getContactItemListByItem(list) {
        return this._search.getContactItemListByItem(list);
    }
    async getContactItemListByContactId(params) {
        return this._search.getContactItemListByContactId(params);
    }
    async getContactItemListByType(contactType) {
        return this._search.getContactItemListByType(contactType);
    }
    async getPersonalOrg(params) {
        return this._search.getPersonalOrg(params);
    }
    async queryPersonalMemberCount(ids, account) {
        return this._search.queryPersonalMemberCount(ids, account);
    }
    async getContactSearchCount(_account) {
        return this._search.getContactSearchCount(_account);
    }
    getColor(email) {
        return this._search.getColor(email);
    }
    async getAllContactList(account) {
        return this._search.getAllContactList(account);
    }
    async getAllOrgContactList(account) {
        return this._search.getAllOrgContactList(account);
    }
    async handleContactList(params) {
        return this._search.handleContactList(params);
    }
    async getContactsInMemoryMode(param) {
        return this._search.getContactsInMemoryMode(param);
    }
    async getPersonalContactsFromMemory(orgId, _account) {
        return this._search.getPersonalContactsFromMemory(orgId, _account);
    }
    transformMemoryModel2StandardModel(item, options) {
        return this._search.transformMemoryModel2StandardModel(item, options);
    }
    async testMemoryDataException() {
        return this._search.testMemoryDataException();
    }
    handleSearchCondition(condition) {
        return this._search.handleSearchCondition(condition);
    }
    getSearchCondition(condition) {
        return this._search.getSearchCondition(condition);
    }
    async getSearchData(params) {
        return this._search.getSearchData(params);
    }
    async handleSearchList(searchList) {
        return this._search.handleSearchList(searchList);
    }
    getContactBySearch(condition) {
        return this._search.getContactBySearch(condition);
    }
    getSearchFilter() {
        return this._search.getSearchFilter();
    }
    searchTable(params) {
        return this._search.searchTable(params);
    }
    transMemorySearchCondition(condition) {
        return this._search.transMemorySearchCondition(condition);
    }
    transSearchCondition(condition) {
        return this._search.transSearchCondition(condition);
    }
    async getContactBySearch2(params) {
        return this._search.getContactBySearch2(params);
    }
    getFlatStrOrArrInfo(arr, type, rs) {
        return this._search.getFlatStrOrArrInfo(arr, type, rs);
    }
    async getContactByItem(condition) {
        return this._search.getContactByItem(condition);
    }
    async getContactByYunxin(accounts, showDisable) {
        return this._search.getContactByYunxin(accounts, showDisable);
    }
    async getTotalLokiData(options) {
        return this._search.getTotalLokiData(options);
    }
    async writeLxInfo(rows, _account) {
        return this._search.writeLxInfo(rows, _account);
    }
    async readLxInfoByIds(contactIds, _account) {
        return this._search.readLxInfoByIds(contactIds, _account);
    }
    async intoTable(options, config) {
        return this._base.intoTable(options, config);
    }
    async getDiffRes(params) {
        return this._base.getDiffRes(params);
    }
    async getTableCount(options) {
        return this._base.getTableCount(options);
    }
    deleteData(options) {
        return this._base.deleteData(options);
    }
    async clearTable(dbName) {
        return this._base.clearTable(dbName);
    }
    async restoreData(dbName, list) {
        return this._base.restoreData(dbName, list);
    }
    getTableList(name, idList, contactType) {
        return this._base.getTableList(name, idList, contactType);
    }
    async setContactList(data) {
        return this._base.setContactList(data);
    }
    async setOrgList(data) {
        return this._base.setOrgList(data);
    }
    async delListByKey(params) {
        return this._base.delListByKey(params);
    }
    async doInsertPersonalMark(options, config) {
        return this._mark.doInsertPersonalMark(options, config);
    }
    async doDeletePersonalMark(params) {
        return this._mark.doDeletePersonalMark(params);
    }
    async getPersonalMarkListByFields(args, field = 'value', _account) {
        return this._mark.getPersonalMarkListByFields(args, field, _account);
    }
    async getMaxPersonalMark(_account) {
        return this._mark.getMaxPersonalMark(_account);
    }
    async doGetPersonalMarklistByEmail(emails) {
        return this._mark.doGetPersonalMarklistByEmail(emails);
    }
    async updatePersonalmarkMailUnreadCount(idMap, _account) {
        return this._mark.updatePersonalmarkMailUnreadCount(idMap, _account);
    }
    async updateContactOrgMarkInfo(params) {
        return this._mark.updateContactOrgMarkInfo(params, res => this.sendContactNotify(res));
    }
    async updateContactOrgMarkInfoWithQuery(params) {
        return this._mark.updateContactOrgMarkInfoWithQuery(params, res => this.sendContactNotify(res));
    }
    async getRecentContact(params) {
        return this._frequent.getRecentContact(params);
    }
    async saveRecentContactInDb(concatList, params) {
        return this._frequent.saveRecentContactInDb(concatList, params);
    }
    async queryFrequentContact(params) {
        return this._frequent.queryFrequentContact(params);
    }
    async putFrequentContact(params) {
        return this._frequent.putFrequentContact(params);
    }
    async cleanExpiredFrequentContact(params) {
        return this._frequent.cleanExpiredFrequentContact(params);
    }
    async sortSearchResult(params) {
        return this._frequent.sortSearchResult(params);
    }
    async personalAllInto(options) {
        return this._personal.personalAllInto(options);
    }
    personalInto(params, _account) {
        return this._personal.personalInto(params, _account);
    }
    async personalOrgAllInto(options) {
        return this._personal.personalOrgAllInto(options);
    }
    async personalContactDelete(params) {
        return this._personal.personalContactDelete(params);
    }
    async doInsertOrReplacePersonal(params) {
        return this._personal.doInsertOrReplacePersonal(params, res => this.sendContactNotify(res));
    }
    async cleanInvalidPersonalContact(params) {
        return this._personal.cleanInvalidPersonalContact(params);
    }
    async clearnInvalidPersonalOrg(params) {
        return this._personal.clearnInvalidPersonalOrg(params);
    }
    async deleteTrashContactByManual() {
        return this._personal.deleteTrashContactByManual();
    }
    personalOrgIntoOrg(list, _lastUpdateTime = Date.now()) {
        return this._personal.personalOrgIntoOrg(list, _lastUpdateTime);
    }
    transOrgIdByPersonalOrg(id) {
        return this._personal.transOrgIdByPersonalOrg(id);
    }
    async updateContactById(params) {
        return this._personal.updateContactById(params);
    }
    async updateContactModel(params) {
        return this._personal.updateContactModel(params);
    }
    async syncTeamList(params = {}) {
        return this._team.syncTeamList(params, res => this.sendContactNotify(res), this.isSyncTeamListRef);
    }
    async getTeamServerData() {
        return this._team.getTeamServerData();
    }
    async getNeedUpdateTeamOrgList(params) {
        return this._team.getNeedUpdateTeamOrgList(params);
    }
    transTeamToOrg(list) {
        return this._team.transTeamToOrg(list);
    }
    transServerTeamMemberList(item) {
        return this._team.transServerTeamMemberList(item);
    }
    transServerTeamList(list) {
        return this._team.transServerTeamList(list);
    }
    async transTeamMemberToOrgContact(list) {
        return this._team.transTeamMemberToOrgContact(list);
    }
    teamIntoOrg(list) {
        return this._team.teamIntoOrg(list);
    }
    async teamMemberIntoTable(teamIdList, list) {
        return this._team.teamMemberIntoTable(teamIdList, list);
    }
    getTeamListById(list) {
        return this._team.getTeamListById(list);
    }
    async getTeamMemberById(idList) {
        return this._team.getTeamMemberById(idList);
    }
    async getTeamMemberByTeamIdAndAccount(teamId, accounts) {
        return this._team.getTeamMemberByTeamIdAndAccount(teamId, accounts);
    }
    getTeamOrgList() {
        return this._team.getTeamOrgList();
    }
    async handleLeaveTeam(team) {
        return this._team.handleLeaveTeam(team);
    }
    async handleUpdateTeam(team) {
        return this._team.handleUpdateTeam(team);
    }
    async handleRemoveTeamMembers(team, accounts) {
        return this._team.handleRemoveTeamMembers(team, accounts);
    }
    async handleTransferTeamMembers(team, accounts) {
        return this._team.handleTransferTeamMembers(team, accounts);
    }
    async handleAddTeamMembers(team, accounts) {
        return this._team.handleAddTeamMembers(team, accounts);
    }
    async handleTeamEvent(res) {
        return this._team.handleTeamEvent(res, r => this.sendContactNotify(r));
    }
    async coreContactAllInto(options, _config) {
        return this._enterprise.coreContactAllInto(options, _config);
    }
    async coreContactIntoChunked(options) {
        return this._enterprise.coreContactIntoChunked(options);
    }
    async coreOrgAllInto(options, config) {
        return this._enterprise.coreOrgAllInto(options, config);
    }
    async enterpriseAllInto(options) {
        return this._enterprise.enterpriseAllInto(options);
    }
    async orgAllInto(options) {
        return this._enterprise.orgAllInto(options);
    }
    orgInto(list) {
        return this._enterprise.orgInto(list);
    }
    async sendCoreDeleteInvalidEvent(params) {
        return this._enterprise.sendCoreDeleteInvalidEvent(params);
    }
    async detectCoreEnterpriseHasData(options) {
        return this._enterprise.detectCoreEnterpriseHasData(options);
    }
    async detectTempContactSyncStatusWithMemory(from) {
        return this._enterprise.detectTempContactSyncStatusWithMemory(from);
    }
    setNeedComputeMemberNum(options) {
        return this._enterprise.setNeedComputeMemberNum(options);
    }
    async triggerComputeEntityOrgMemeberNum(account) {
        return this._enterprise.triggerComputeEntityOrgMemeberNum(account);
    }
    async hasSubDomainData(orgId) {
        return this._enterprise.hasSubDomainData(orgId);
    }
    async deleteCustomerDataInDB() {
        return this._enterprise.deleteCustomerDataInDB();
    }
    async contactDetailInto(lokiApi, contacts, contactItems, lxInfoRows, _account) {
        return this._enterprise.contactDetailInto(lokiApi, contacts, contactItems, lxInfoRows, _account);
    }
    async searchResultInto(lokiApi, contacts, contactItems, _account) {
        return this._enterprise.searchResultInto(lokiApi, contacts, contactItems, _account);
    }
    async deleteOrgContactList(params) {
        const { list, _account } = params;
        const contactIdList = (await import('@/api/util')).util.getKeyListByList(list, 'contactId', true);
        const orgContactList = await this.getOrgContactList({
            idList: contactIdList,
            type: 'contactId',
            filterTeamOrg: true,
        });
        const orgContactIdList = (await import('@/api/util')).util.getKeyListByList(orgContactList, 'id');
        await this.deleteData({
            tableName: this.contactUtil.orgContactTable,
            list: orgContactIdList,
            _account,
        });
        return {
            orgContact_enterprise: {
                deleteDiff: orgContactIdList,
            },
        };
    }
    async deleteContactItemList(params) {
        var _a;
        const { util } = await import('@/api/util');
        const { list, type = 'enterprise', _account } = params;
        const tableType = 'contactItem_' + type;
        const res = {
            [tableType]: {},
        };
        try {
            const contactIdList = util.getKeyListByList(list, 'contactId', true);
            const idList = util.getKeyListByList(list, 'id');
            let contactItemList;
            if (type === 'enterprise') {
                contactItemList = await this.getContactItemListByContactId({
                    idList: contactIdList,
                });
            }
            else {
                contactItemList = await this.getContactItemListByType(type);
            }
            const oldIdList = util.getKeyListByList(contactItemList, 'id');
            const diffResult = util.getDiff(oldIdList, idList);
            ((_a = diffResult.deleteDiff) === null || _a === void 0 ? void 0 : _a.length) &&
                (await this.deleteData({
                    tableName: this.contactUtil.contactItemTable,
                    list: diffResult.deleteDiff,
                    _account,
                }));
            res[tableType] = diffResult;
        }
        catch (e) {
            console.error('[contact] deleteContactItemList error', e, list, type);
        }
        return res;
    }
    init() {
        var _a;
        const wasInited = this.inited;
        this.inited = this._enterprise.init(this.inited, () => this.initSyncNIM(), () => this.initContactData(), () => this.testDbNoData(), (val) => this._search.setEnablePutContact(val));
        if (!wasInited) {
            const mainAccount = ((_a = this.systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.id) || '';
            getContactIMHelperInstance()
                .backfillTeamsFromDexie(mainAccount)
                .then(backfilled => {
                if (backfilled) {
                    return;
                }
                return getContactIMHelperInstance()
                    .checkNeedFullSync(mainAccount)
                    .then(needFullSync => {
                    if (needFullSync) {
                        getContactIMHelperInstance().syncAllTeams(mainAccount);
                    }
                });
            })
                .catch(e => {
                console.error('[contact] init backfillTeamsFromDexie error', e);
            });
            backfillLxInfoFromDexie(mainAccount)
                .then(backfilled => {
                if (backfilled) {
                    this.sendContactNotify({
                        syncStatus: { enterprise: true },
                        isAll: true,
                        _account: mainAccount,
                    });
                }
            })
                .catch(e => {
                console.error('[contact] init backfillLxInfoFromDexie error', e);
            });
        }
    }
    async testDbNoData() {
        let force = true;
        try {
            const data = await Promise.all([
                this._base.getTableCount({ tableName: 'contact' }),
                this._base.getTableCount({ tableName: 'orgContact' }),
                this._base.getTableCount({ tableName: 'contactItem' }),
            ]);
            console.log('[contact] testDbNoData table count contact,org', data);
            force = !data.some(item => item > 0);
        }
        catch (error) {
            console.error('[contact] testDbNoData table count Error', error);
            force = true;
        }
        return force;
    }
    initSyncNIM() {
        var _a;
        const func = (e) => {
            const eventData = e.eventData;
            this.handleTeamEvent(eventData);
        };
        this.eventApi.registerSysEventObserver('imTeamEvents', {
            func,
            _account: ((_a = this.systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.id) || '',
        });
    }
    sendContactNotify(res) {
        this.setContactLastUpdateTime();
        this.contactSyncTimes += 1;
        res.contactSyncTimes = this.contactSyncTimes;
        res.hasDiff = true;
        dispatchContactSyncEvents(this.eventApi, res, this.systemApi);
    }
    close() {
        this.clearCache();
        api.requireLogicalApi(apis.dbInterfaceApiImpl).closeSpecific(this.contactUtil.contactDbName);
    }
    clearCache() {
        console.log('[contact] clearCache');
    }
    async initCache() {
        console.log('[contact] initCache');
    }
    initContactData() {
        console.log('[contact] initModule send contact');
        this.eventApi.sendSysEvent({
            eventName: 'initModule',
            eventStrData: 'contact',
        });
    }
}
export const ContactDBInstance = new ContactDB();
